import { useEffect, useRef, useState } from "react";
import { Loader2, MapPin } from "lucide-react";

export interface Coords { lat: number; lng: number; }

export const geocodeAddress = async (address: string): Promise<Coords | null> => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1&countrycodes=pt`,
      { headers: { "Accept-Language": "pt" } }
    );
    const data = await res.json();
    if (data.length > 0) return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
    return null;
  } catch {
    return null;
  }
};

const ensureLeafletCss = () => {
  if (typeof document === "undefined") return;
  if (!document.getElementById("leaflet-css")) {
    const link = document.createElement("link");
    link.id = "leaflet-css";
    link.rel = "stylesheet";
    link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(link);
  }
};

interface Props {
  coords: Coords | null;
  label?: string;
  loading?: boolean;
  height?: number;
}

const MiniMap = ({ coords, label = "", loading, height = 200 }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);

  useEffect(() => {
    let cancelled = false;
    if (!coords || !ref.current) return;
    ensureLeafletCss();
    (async () => {
      const L = await import("leaflet");
      if (cancelled || !ref.current) return;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      const map = L.map(ref.current, { zoomControl: true, attributionControl: false }).setView(
        [coords.lat, coords.lng], 15
      );
      mapRef.current = map;
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(map);
      const icon = L.divIcon({
        html: '<div style="background:hsl(160,30%,40%);width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>',
        iconSize: [28, 28],
        iconAnchor: [14, 28],
        className: "",
      });
      const marker = L.marker([coords.lat, coords.lng], { icon }).addTo(map);
      if (label) marker.bindPopup(label);
      setTimeout(() => map.invalidateSize(), 150);
    })();
    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [coords?.lat, coords?.lng, label]);

  return (
    <div className="relative rounded-xl overflow-hidden border border-border" style={{ height }}>
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted/70">
          <Loader2 size={18} className="animate-spin text-muted-foreground" />
        </div>
      )}
      {!coords && !loading && (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground bg-muted/40">
          <MapPin size={14} className="mr-1.5" /> Sem localização no mapa
        </div>
      )}
      <div ref={ref} className="w-full h-full" />
    </div>
  );
};

export default MiniMap;
